import {
    getNewBasicOrderJsonSchema,
    getGenericDefinitionsAsJsonSchemaDefinitions,
    convertInlineDefsToRefs,
} from '../newBasicOrderJsonSchema';
import { log as logger } from './log';
import { RuleEffect, resolveSchema } from '@jsonforms/core';
import type { Rule, JsonSchema, GroupLayout, ControlElement } from '@jsonforms/core';
import type { PermissionType } from '@thinkalpha/common/contracts/permissions.js';
import type { Instrument } from '@thinkalpha/common/schemas/instruments.js';
import { doesAccessGrantAllowRequest } from '@thinkalpha/common/util/permissions.js';
import type { OrderType } from '@thinkalpha/trading-ws-client/contracts/order.js';
import { isEqual } from 'es-toolkit';
import { get, isEmpty } from 'es-toolkit/compat';
import { t } from 'i18next';
import type { KeysWithoutReturnObjects } from 'i18next';
import type { GridCollapsibleLayout } from 'src/components/JsonForm/layouts/GridCollapsibleLayout';
import type { GridLayout } from 'src/components/JsonForm/layouts/GridLayout';
import { fakeRequired } from 'src/components/JsonForm/ThinkalphaTheme';
import {
    QUEUE_FOR_REMOVAL_FROM_ORDER_DATA_LABEL,
    QUEUE_FOR_REMOVAL_FROM_ORDER_DATA_VALUE,
} from 'src/lib/orders/removePropsFromOrderData';
import { accountRequiresFiltering } from 'src/lib/trading/destinationFilter/filterDestinations';
import { DEFAULT_EXDESTINATION, DEFAULT_ROUTE } from 'src/models/TradeProWidgetModel';
import { isPropertyForbiddenBySchema } from 'src/routes/widgets/TradeProWidget/state/schemaConditions';
import type { BaseSchema, OrderData, TradeProState } from 'src/routes/widgets/TradeProWidget/state/types';
import { safeCloneSchema } from 'src/routes/widgets/TradeProWidget/state/utils';

const log = logger.getSubLogger({ name: 'JsonFormGeneratorService' }, { component: 'trading' });

const HideRule = {
    effect: RuleEffect.HIDE,
    // If the scope resolves to undefined the condition is true
    condition: {
        scope: '',
        schema: {},
    },
} as Rule;

interface RefinedSchemaGroups {
    mandatoryGroup: GridLayout;
    nonMandatoryGroup: GridCollapsibleLayout;
    byCategories: GridCollapsibleLayout[];
}

export const PriceVisibilityMap: Record<OrderType, boolean> = {
    market: false,
    limit: true,
    stop: false,
    stop_limit: true,
    pegged: true,
    box_limit: true,
    box_market: false,
};
export const StopPriceVisibilityMap: Record<OrderType, boolean> = {
    market: false,
    limit: false,
    stop: true,
    stop_limit: true,
    pegged: false,
    box_limit: false,
    box_market: false,
};

export interface GenerateUISchemaProps {
    schema: any;
    path?: string;
    ignoredProps?: string[];
    hiddenProps?: string[];
    orderedProps?: string[];
    fakeRequired?: string[];
    orderData: OrderData;
    selectedAccountEffectiveAccess: PermissionType;
}

export interface JsonFormGeneratorService {
    generateSchema(state: TradeProState): JsonSchema;

    generateUISchema(props: GenerateUISchemaProps): GroupLayout;
}

export class JsonFormGeneratorServiceImpl implements JsonFormGeneratorService {
    generateSchema(state: TradeProState): JsonSchema {
        const baseSchema = state.executorsSchema;
        const schemaForExchange = state.selectedExecutorSchema;
        const selectedExDestination = state.orderData.exDestination;
        const selectedRoute = state.orderData.route;
        const exDestinationsWithRoutes = state.exDestinationsWithRoutes;
        const orderData = state.orderData;
        const availableRoutes = Object.keys(schemaForExchange);

        // Basic schema
        if (this.isBasicSchema(baseSchema)) {
            const schema = safeCloneSchema(baseSchema);
            schema.properties.exDestination = {
                type: 'string',
                enum: [DEFAULT_EXDESTINATION],
            };
            schema.properties.route = {
                type: 'string',
                const: DEFAULT_ROUTE,
            };

            let refined = schema;
            refined = this.#refineDuration(refined, state.type, orderData.type);
            refined = this.#refineOpenClose(refined, orderData?.instrument);
            refined = this.#refineEnumsAndOneOfs(refined);

            return refined;
        }

        const isRoutelessSchema =
            !schemaForExchange ||
            isEmpty(schemaForExchange) ||
            (availableRoutes.length === 1 && availableRoutes.includes(DEFAULT_ROUTE)) ||
            !selectedRoute;

        if (isRoutelessSchema) {
            const resolvedSchema = this.#resolveRoutelessSchema(baseSchema, selectedExDestination);
            const basicSchema = resolvedSchema ?? getNewBasicOrderJsonSchema();
            let refined = convertInlineDefsToRefs(basicSchema);
            // Create a new ref for $defs to avoid modifying the original basic schema
            (refined as any).$defs = getGenericDefinitionsAsJsonSchemaDefinitions();

            refined = this.#setExDestinationAndRouteProperties(
                refined,
                baseSchema,
                schemaForExchange,
                exDestinationsWithRoutes,
                selectedExDestination,
                selectedRoute,
                orderData.account,
            );

            refined = this.#refineDuration(refined, state.type, orderData.type);
            refined = this.#refineOpenClose(refined, orderData?.instrument);
            refined = this.#refineEnumsAndOneOfs(refined);
            return refined;
        }

        let refined = this.#refineExDestinationAndRoute(
            baseSchema,
            schemaForExchange,
            exDestinationsWithRoutes,
            selectedExDestination,
            selectedRoute,
            orderData.account,
        );
        refined = this.#refineDuration(refined, state.type, orderData.type);
        refined = this.#refineOpenClose(refined, orderData?.instrument);
        refined = this.#refineEnumsAndOneOfs(refined);

        return refined;
    }

    /**
     * Sets exDestination and route properties on the schema.
     * Ensures properties object exists and configures the exDestination enum and route schema.
     */
    #setExDestinationAndRouteProperties(
        schema: any,
        baseSchema: any,
        schemaForExchange: Record<string, any>,
        exDestinationsWithRoutes: Record<string, any>,
        selectedExDestination: string,
        selectedRoute: string,
        account: string,
    ): any {
        const shouldReplaceSchema = schemaForExchange[selectedRoute] || !schema.properties;

        if (shouldReplaceSchema) {
            // Use route-specific schema if available, otherwise generate fallback
            schema = schemaForExchange[selectedRoute]
                ? safeCloneSchema(schemaForExchange[selectedRoute])
                : convertInlineDefsToRefs(getNewBasicOrderJsonSchema());
        }

        // TODO: Test fallback!!
        schema.$defs = baseSchema.$defs ?? getGenericDefinitionsAsJsonSchemaDefinitions();

        // Ensure properties object exists
        if (!schema.properties) {
            schema.properties = {};
        }

        const exDestination = safeCloneSchema(baseSchema.properties?.exDestination || { type: 'string', enum: [] });

        // Use passed exDestinationWithRoutes for the final enum as some exDestinations/routes may not be
        // available for the selected instrument type, and they are filtered out
        exDestination.enum = Object.keys(exDestinationsWithRoutes) || [];

        // Only add DEFAULT_EXDESTINATION ('basic') if the account doesn't have destination filtering
        // For accounts with filtering (e.g., AVTR001, AVTR016), they should only see their allowed destinations
        // See PLAT-11238 for context
        if (!accountRequiresFiltering(account) && !exDestination.enum.includes(DEFAULT_EXDESTINATION)) {
            exDestination.enum.unshift(DEFAULT_EXDESTINATION);
        }
        schema.properties.exDestination = exDestination;
        schema.properties.route =
            selectedExDestination === DEFAULT_EXDESTINATION
                ? {
                      type: 'string',
                      const: DEFAULT_ROUTE,
                  }
                : {
                      type: 'string',
                      enum: exDestinationsWithRoutes[selectedExDestination],
                  };

        return schema;
    }

    #refineExDestinationAndRoute(
        baseSchema: any,
        schemaForExchange: Record<string, any>,
        exDestinationsWithRoutes: Record<string, any>,
        selectedExDestination: string,
        selectedRoute: string,
        account: string,
    ): any {
        let schema;
        if (schemaForExchange[selectedRoute]) {
            // Use the provided route schema
            schema = safeCloneSchema(schemaForExchange[selectedRoute]);
        } else {
            // Fallback: generate schema from Zod and convert inline defs to refs
            schema = convertInlineDefsToRefs(getNewBasicOrderJsonSchema());
        }

        // TODO: Test fallback!!
        schema.$defs = baseSchema.$defs ?? getGenericDefinitionsAsJsonSchemaDefinitions();

        schema = this.#setExDestinationAndRouteProperties(
            schema,
            baseSchema,
            schemaForExchange,
            exDestinationsWithRoutes,
            selectedExDestination,
            selectedRoute,
            account,
        );

        /**
         * Hard-coded magic to allow exDestination and route to have the DEFAULT_EXDESTINATION value.
         * Basically removing the conditions where both fields should not be defined
         * or have the 'basic' (DEFAULT_EXDESTINATION/DEFAULT_ROUTE) value. We will remove the fields from the
         * form data object before the data is sent to the API as they should not be
         * part of it if their value equals the value of DEFAULT_EXDESTINATION / DEFAULT_ROUTE const
         */
        if (
            selectedExDestination === DEFAULT_EXDESTINATION &&
            selectedRoute === DEFAULT_ROUTE &&
            Array.isArray(schema.allOf)
        ) {
            schema.allOf = schema.allOf.filter(
                (rule: any) =>
                    !rule?.allOf?.some((allOfItem: any) =>
                        allOfItem?.anyOf?.some((anyOfItem: any) => anyOfItem?.not?.required?.includes('exDestination')),
                    ),
            );
        }

        return schema;
    }

    #refineDuration(schema: any, type: string, orderDataType: string): any {
        const shouldRemoveFOK = orderDataType === 'market' || !('duration' in (schema?.properties || {}));
        const shouldRemoveGTDandGTC = type === 'multi-leg';

        if (!shouldRemoveFOK && !shouldRemoveGTDandGTC) {
            return schema;
        }

        const clonedSchema = safeCloneSchema(schema);

        // The duration is using the generic definition
        if (clonedSchema.properties.duration?.$ref === '#/$defs/generic/orderDuration') {
            const existingOneOf = clonedSchema.$defs.generic.orderDuration.oneOf;
            clonedSchema.properties.duration.$ref = '#/$defs/refined/orderDuration';

            let filteredOneOf = existingOneOf;

            if (shouldRemoveFOK) {
                filteredOneOf = filteredOneOf.filter(
                    (condition: { const: string; type: string }) => condition.const !== 'fok',
                );
            }

            if (shouldRemoveGTDandGTC) {
                filteredOneOf = filteredOneOf.filter(
                    (condition: { const: string; type: string }) =>
                        condition.const !== 'gtd' && condition.const !== 'gtc',
                );
            }

            clonedSchema.$defs.refined = {
                ...(clonedSchema.$defs.refined || {}),
                orderDuration: {
                    oneOf: filteredOneOf,
                },
            };
        } else if (Array.isArray(clonedSchema.properties.duration?.oneOf)) {
            let filteredOneOf = clonedSchema.properties.duration.oneOf;

            if (shouldRemoveFOK) {
                filteredOneOf = filteredOneOf.filter(
                    (condition: { const: string; type: string }) => condition.const !== 'fok',
                );
            }

            if (shouldRemoveGTDandGTC) {
                filteredOneOf = filteredOneOf.filter(
                    (condition: { const: string; type: string }) =>
                        condition.const !== 'gtd' && condition.const !== 'gtc',
                );
            }

            clonedSchema.properties.duration.oneOf = filteredOneOf;
        } else if (Array.isArray(clonedSchema.properties.duration?.enum)) {
            let filteredEnum = clonedSchema.properties.duration.enum;

            if (shouldRemoveFOK) {
                filteredEnum = filteredEnum.filter((value: string) => value !== 'fok');
            }

            if (shouldRemoveGTDandGTC) {
                filteredEnum = filteredEnum.filter((value: string) => value !== 'gtd' && value !== 'gtc');
            }

            clonedSchema.properties.duration.enum = filteredEnum;
        } else {
            log.error('Unhandled case for refining duration in schema', { duration: clonedSchema.properties.duration });
        }

        return clonedSchema;
    }

    #refineOpenClose(schema: any, instrument?: Instrument): any {
        const hasOpenCloseInSchema = get(schema, 'properties.openClose');
        const isOptionInstrument = instrument?.type === 'option';

        // Remove OpenClose from the schema for non-option instruments
        const shouldRemoveOpenClose = !isOptionInstrument && hasOpenCloseInSchema;

        if (shouldRemoveOpenClose) {
            const clonedSchema = structuredClone(schema);

            if (TRACE) {
                log.trace('Removing OpenClose from schema as instrument is not an option', {
                    instrumentType: instrument?.type,
                    schemaBefore: schema,
                });
            }

            delete clonedSchema.properties.openClose;

            return clonedSchema;
        }

        // For option instruments with openClose in schema, relax the required constraint
        // to prevent validation errors before the "Buy to Open/Close" buttons are clicked.
        // This matches the UI condition for showing split buttons (TradeProFormButtons.tsx:28-30).
        const shouldRelaxOpenCloseRequirement = isOptionInstrument && hasOpenCloseInSchema && schema.allOf;

        if (shouldRelaxOpenCloseRequirement) {
            const clonedSchema = structuredClone(schema);

            if (TRACE) {
                log.trace('Relaxing openClose required constraint for option instrument', {
                    instrumentType: instrument?.type,
                });
            }

            // Modify the allOf array to remove the 'required' constraint from openCloseCondition
            clonedSchema.allOf = clonedSchema.allOf.map((condition: any) => {
                // Identify the openCloseCondition by checking if it's the conditional that makes
                // openClose required for option instruments
                if (
                    condition.if?.properties?.instrument?.properties?.type?.const === 'option' ||
                    condition.if?.properties?.instrument?.properties?.type?.enum?.[0] === 'option'
                ) {
                    // Remove the 'required' constraint from the 'then' clause
                    const modifiedCondition = structuredClone(condition);
                    if (modifiedCondition.then?.required) {
                        modifiedCondition.then.required = modifiedCondition.then.required.filter(
                            (field: string) => field !== 'openClose',
                        );
                        // If required array is now empty, remove the property
                        if (modifiedCondition.then.required.length === 0) {
                            delete modifiedCondition.then.required;
                        }
                    }
                    return modifiedCondition;
                }
                return condition;
            });

            return clonedSchema;
        }

        return schema;
    }

    #refineEnumsAndOneOfs(schema: any): any {
        // Add no-value-selected option to the top of enums and oneOfs for non-required properties
        const clonedSchema = safeCloneSchema(schema);

        // Collect all properties that have conditional validation rules
        // This is done once per schema to avoid repeated traversals
        const getPropertiesWithConditionalValidation = (sch: Record<string, any>): Set<string> => {
            const propsWithConditionals = new Set<string>();

            // Collect all potential conditional arrays
            const conditionalArrays = [];
            if (sch.allOf && Array.isArray(sch.allOf)) {
                conditionalArrays.push(sch.allOf);
            }
            if (sch.anyOf && Array.isArray(sch.anyOf)) {
                conditionalArrays.push(sch.anyOf);
            }
            if (sch.oneOf && Array.isArray(sch.oneOf)) {
                conditionalArrays.push(sch.oneOf);
            }

            if (conditionalArrays.length === 0) {
                return propsWithConditionals;
            }

            const checkForPropsInCondition = (condition: any): string[] => {
                if (!condition) return [];

                const foundProps: string[] = [];

                // Collect properties that are required in the condition
                if (condition.required && Array.isArray(condition.required)) {
                    foundProps.push(...condition.required);
                }

                // Collect properties that have validation rules in the condition
                if (condition.properties && typeof condition.properties === 'object') {
                    foundProps.push(...Object.keys(condition.properties));
                }

                // Check anyOf/allOf/oneOf recursively
                if (condition.anyOf || condition.allOf || condition.oneOf) {
                    const subConditions = condition.anyOf || condition.allOf || condition.oneOf;
                    for (const sub of subConditions) {
                        foundProps.push(...checkForPropsInCondition(sub));
                    }
                }

                return foundProps;
            };

            // Traverse all conditional arrays and collect properties
            for (const arr of conditionalArrays) {
                for (const rule of arr) {
                    if (!rule.if && !rule.then && !rule.else) {
                        continue;
                    }

                    const propsInIf = checkForPropsInCondition(rule.if);
                    const propsInThen = checkForPropsInCondition(rule.then);
                    const propsInElse = checkForPropsInCondition(rule.else);

                    [...propsInIf, ...propsInThen, ...propsInElse].forEach((prop) => propsWithConditionals.add(prop));
                }
            }

            return propsWithConditionals;
        };

        const processProperties = (sch: Record<string, any>) => {
            const props = sch.properties || {};
            const required = [...(sch.required ?? []), ...fakeRequired];

            // Collect properties with conditional validation once, before the loop
            const propsWithConditionals = getPropertiesWithConditionalValidation(sch);

            for (const prop in props) {
                // The instrument is set by a custom input outside the form; never add the
                // no-value option to it or to its inline sub-properties.
                if (prop === 'instrument') {
                    continue;
                }

                if (
                    props[prop] &&
                    typeof props[prop] === 'object' &&
                    props[prop].type === 'object' &&
                    props[prop].properties
                ) {
                    processProperties(props[prop]);
                } else if (!required.includes(prop)) {
                    const hasConditional = propsWithConditionals.has(prop);
                    const hasEnumOrOneOf = Array.isArray(props[prop]?.oneOf) || Array.isArray(props[prop]?.enum);

                    // If property has conditional validation, mark it with metadata but don't modify the schema
                    if (hasConditional && hasEnumOrOneOf) {
                        // Add metadata to indicate this field allows being unset in the UI
                        props[prop]['x-allow-unset'] = true;
                        continue;
                    }

                    if (
                        Array.isArray(props[prop]?.oneOf ?? null) &&
                        !props[prop]?.oneOf?.find(
                            (item: any) => item?.const === QUEUE_FOR_REMOVAL_FROM_ORDER_DATA_VALUE,
                        )
                    ) {
                        props[prop].oneOf.unshift({
                            title: QUEUE_FOR_REMOVAL_FROM_ORDER_DATA_LABEL,
                            const: QUEUE_FOR_REMOVAL_FROM_ORDER_DATA_VALUE,
                            type: 'string',
                        });
                    } else if (
                        Array.isArray(props[prop]?.enum ?? null) &&
                        !props[prop]?.enum?.includes(QUEUE_FOR_REMOVAL_FROM_ORDER_DATA_LABEL)
                    ) {
                        props[prop].enum.unshift(QUEUE_FOR_REMOVAL_FROM_ORDER_DATA_LABEL);
                    }
                }
            }
        };

        processProperties(clonedSchema);

        if (isEqual(schema, clonedSchema)) {
            return schema;
        }

        return clonedSchema;
    }

    generateUISchema = ({
        schema,
        path = '#',
        ignoredProps = [],
        hiddenProps = [],
        orderedProps = [],
        fakeRequired = [],
        orderData,
        selectedAccountEffectiveAccess,
    }: GenerateUISchemaProps): GroupLayout => {
        const mandatoryGroup: GridLayout = {
            type: 'GridLayout',
            elements: [],
        };
        const nonMandatoryGroup: GridCollapsibleLayout = {
            type: 'GridCollapsibleLayout',
            label: 'Optional Instructions',
            elements: [],
        };
        const byCategories: { [key: string]: GridCollapsibleLayout } = {};

        Object.keys(schema.properties ?? []).forEach((prop) => {
            if (ignoredProps.includes(prop)) {
                return;
            }

            if (schema.properties[prop]?.type === 'object') {
                const nestedElements = this.generateUISchema({
                    schema: schema.properties[prop],
                    path: `${path}/properties/${prop}`,
                    ignoredProps,
                    hiddenProps,
                    orderedProps,
                    fakeRequired,
                    orderData,
                    selectedAccountEffectiveAccess,
                });

                const [nestedMandatory, nestedNonMandatory, ...nestedCategories] = nestedElements.elements as [
                    GridLayout,
                    GridCollapsibleLayout,
                    ...GridCollapsibleLayout[],
                ];

                mandatoryGroup.elements = [...mandatoryGroup.elements, ...nestedMandatory.elements] as ControlElement[];

                nonMandatoryGroup.elements = [
                    ...nonMandatoryGroup.elements,
                    ...nestedNonMandatory.elements,
                ] as ControlElement[];

                nestedCategories?.forEach((category) => {
                    if (byCategories[category.label]) {
                        byCategories[category.label].elements = [
                            ...byCategories[category.label].elements,
                            ...category.elements,
                        ] as ControlElement[];
                    } else {
                        byCategories[category.label] = category;
                    }
                });

                return;
            }

            const element = {
                type: 'Control',
                scope: `${path}/properties/${prop}`,
            } as ControlElement;

            if (hiddenProps.includes(prop)) {
                element.rule = HideRule;
            }

            if ((schema.required || []).includes(prop) || fakeRequired.includes(prop)) {
                mandatoryGroup.elements.push(element);
            } else if ('x-category' in schema.properties[prop]) {
                const xCategory = schema.properties[prop]['x-category'];
                const label: string = t(
                    `trading::categories::${xCategory}` as KeysWithoutReturnObjects['json'],
                    xCategory,
                ) as unknown as string;
                byCategories[schema.properties[prop]['x-category']] ??= {
                    type: 'GridCollapsibleLayout',
                    label,
                    elements: [],
                };

                byCategories[schema.properties[prop]['x-category']].elements.push(element);
            } else {
                nonMandatoryGroup.elements.push(element);
            }
        });

        mandatoryGroup.elements = this.sortElements(mandatoryGroup.elements as ControlElement[], orderedProps);
        nonMandatoryGroup.elements = this.sortElements(nonMandatoryGroup.elements as ControlElement[], orderedProps);

        const groupedByCategories = Object.values(byCategories);

        let refined = this.#refineExpiryDateUI(
            {
                mandatoryGroup,
                nonMandatoryGroup,
                byCategories: groupedByCategories,
            },
            schema,
            orderData,
        );

        refined = this.#refineRouteUI(refined, schema);
        refined = this.#refineManualReviewUI(refined, selectedAccountEffectiveAccess);
        refined = this.#refineInstrumentUI(refined);
        refined = this.#refinePricesUI(refined, orderData.type);

        const elements: (GridLayout | GroupLayout | GridCollapsibleLayout)[] = [
            refined.mandatoryGroup,
            refined.nonMandatoryGroup,
            ...refined.byCategories,
        ];

        // Add the Slot only to the main loop at the right place
        if (path === '#') {
            elements.splice(1, 0, {
                type: 'Group',
                elements: [
                    {
                        type: 'Slot',
                        options: {
                            slot: 'submitButtonsWrapper',
                        },
                    },
                ],
            } as GroupLayout);
        }

        return {
            type: 'Group',
            elements,
        };
    };

    // Venue schemas decide when expireTime is allowed; hide it only when a conditional rule forbids it
    #refineExpiryDateUI(groups: RefinedSchemaGroups, schema: JsonSchema, orderData: OrderData): RefinedSchemaGroups {
        const isForbidden = isPropertyForbiddenBySchema(schema, orderData, 'expireTime');
        const scope = '#/properties/expireTime';

        const allGroups = [groups.mandatoryGroup, groups.nonMandatoryGroup, ...groups.byCategories];
        const expiryDateElement = allGroups
            .flatMap((group) => group.elements as ControlElement[])
            .find((element) => element.scope === scope);

        if (expiryDateElement) {
            allGroups.forEach((group) => {
                group.elements = (group.elements as ControlElement[]).filter((element) => element.scope !== scope);
            });
            groups.mandatoryGroup.elements.push({
                ...expiryDateElement,
                ...(isForbidden ? { rule: HideRule } : {}),
                options: {
                    format: 'date-time',
                },
            });
        }

        return groups;
    }

    #refineRouteUI(refined: RefinedSchemaGroups, schema: JsonSchema): RefinedSchemaGroups {
        // Hide the Route if there is only one option and is the default one
        const isOneEnumWithDefault =
            (schema?.properties?.route?.enum ?? []).length === 1 &&
            schema?.properties?.route?.enum?.includes(DEFAULT_ROUTE);
        const isConstWithDefault = schema?.properties?.route?.const === DEFAULT_ROUTE;

        if (!isOneEnumWithDefault && !isConstWithDefault) {
            return refined;
        }

        refined.mandatoryGroup.elements = refined.mandatoryGroup.elements.map((element: ControlElement) => {
            if (element.scope === '#/properties/route') {
                return {
                    ...element,
                    rule: HideRule,
                };
            }
            return element;
        });

        return refined;
    }

    #refineManualReviewUI(
        schema: RefinedSchemaGroups,
        selectedAccountEffectiveAccess: PermissionType,
    ): RefinedSchemaGroups {
        const canSeeManualReview = doesAccessGrantAllowRequest(selectedAccountEffectiveAccess, 'admin');

        if (!canSeeManualReview) {
            const scope = '#/properties/tags/properties/AE_ManualReview';

            schema.mandatoryGroup.elements = schema.mandatoryGroup.elements.filter(
                (element: ControlElement) => element.scope !== scope,
            );

            schema.nonMandatoryGroup.elements = schema.nonMandatoryGroup.elements.filter(
                (element: ControlElement) => element.scope !== scope,
            );

            schema.byCategories.forEach((category) => {
                category.elements = category.elements.filter((element: ControlElement) => element.scope !== scope);
            });
        }

        return schema;
    }

    #refineInstrumentUI(schema: RefinedSchemaGroups): RefinedSchemaGroups {
        // The instrument is rendered by a custom input outside the form. Hide the instrument
        // control itself ($ref schema) and any flattened sub-controls (inline object schema).
        const isInstrumentControl = (element: ControlElement) =>
            element.scope === '#/properties/instrument' || element.scope.startsWith('#/properties/instrument/');

        const hideInstrument = (elements: ControlElement[]) =>
            elements.map((element) => (isInstrumentControl(element) ? { ...element, rule: HideRule } : element));

        schema.mandatoryGroup.elements = hideInstrument(schema.mandatoryGroup.elements as ControlElement[]);
        schema.nonMandatoryGroup.elements = hideInstrument(schema.nonMandatoryGroup.elements as ControlElement[]);
        schema.byCategories.forEach((category) => {
            category.elements = hideInstrument(category.elements as ControlElement[]);
        });

        return schema;
    }

    #refinePricesUI(schema: RefinedSchemaGroups, orderType: OrderType | string): RefinedSchemaGroups {
        const showPriceDefault = true;
        const showStopPriceDefault = false;

        schema.mandatoryGroup.elements = schema.mandatoryGroup.elements.map((element: ControlElement) => {
            if (element.scope === '#/properties/price') {
                const shouldShowPrice = (orderType && PriceVisibilityMap[orderType as OrderType]) ?? showPriceDefault;

                if (!shouldShowPrice) {
                    return {
                        ...element,
                        rule: HideRule,
                    };
                }
            }
            if (element.scope === '#/properties/stopPrice') {
                const shouldShowStopPrice =
                    (orderType && StopPriceVisibilityMap[orderType as OrderType]) ?? showStopPriceDefault;

                if (!shouldShowStopPrice) {
                    return {
                        ...element,
                        rule: HideRule,
                    };
                }
            }

            return element;
        });

        return schema;
    }

    private sortElements(elements: ControlElement[], orderedProps: string[]): ControlElement[] {
        return elements.sort((a, b) => {
            const aScope = a.scope?.split('/');
            const aName = aScope?.pop() ?? '';
            const bScope = b.scope?.split('/');
            const bName = bScope?.pop() ?? '';
            const aIdx = orderedProps.indexOf(aName);
            const bIdx = orderedProps.indexOf(bName);

            if (aIdx === -1 && bIdx === -1) return 0;
            if (aIdx === -1) return 1;
            if (bIdx === -1) return -1;
            return aIdx - bIdx;
        });
    }

    /**
     * Resolves the route-less basic schema for a specific exDestination from the baseSchema's oneOf conditionals.
     *
     * The schema structure supports two patterns:
     *
     * Pattern 1 (Simple - most exDestinations like JPM, CANACCORD):
     * - `if.properties.exDestination.const` = exDestination name
     * - `then.oneOf` array with nested conditionals for each route
     * - Within that nested oneOf, route-less conditional with:
     *   - `if.allOf[0].properties.exDestination.const` = exDestination name
     *   - `if.allOf[1].not.required` includes 'route'
     *   - `then.$ref` points to the basic schema to use
     *
     * Pattern 2 (Complex - "basic" exDestination):
     * - `if.allOf[*].anyOf[*].properties.exDestination.const` = exDestination name
     * - `then.$ref` directly points to the basic schema (no nested oneOf)
     *
     * Uses @jsonforms/core's resolveSchema to resolve the $ref.
     *
     * @param baseSchema The full schema containing the oneOf conditionals
     * @param exDestination The selected exDestination (e.g., 'CANACCORD', 'UBS', 'basic')
     * @returns The resolved basic schema or null if not found
     */
    #resolveRoutelessSchema(baseSchema: any, exDestination: string): any | null {
        if (!baseSchema?.oneOf || !Array.isArray(baseSchema.oneOf)) {
            return null;
        }

        // Helper function to check if a conditional matches the exDestination
        const matchesExDestination = (conditional: any): boolean => {
            // Pattern 1: Simple - if.properties.exDestination.const
            if (conditional?.if?.properties?.exDestination?.const === exDestination) {
                return true;
            }

            // Pattern 2: Complex - if.allOf[*].anyOf[*].properties.exDestination.const
            if (conditional?.if?.allOf && Array.isArray(conditional.if.allOf)) {
                return conditional.if.allOf.some((allOfItem: any) => {
                    if (allOfItem?.anyOf && Array.isArray(allOfItem.anyOf)) {
                        return allOfItem.anyOf.some((anyOfItem: any) => {
                            return anyOfItem?.properties?.exDestination?.const === exDestination;
                        });
                    }
                    return false;
                });
            }

            return false;
        };

        // Find the oneOf entry for the selected exDestination
        const exDestinationConditional = baseSchema.oneOf.find(matchesExDestination);

        if (!exDestinationConditional) {
            return null;
        }

        // Pattern 2: Complex pattern with direct $ref in then (e.g., "basic" exDestination)
        if (exDestinationConditional.then?.$ref) {
            const ref = exDestinationConditional.then.$ref;
            const refSchema = { $ref: ref };
            const resolvedSchema = resolveSchema(refSchema as JsonSchema, '', baseSchema);

            if (TRACE) {
                log.trace('Resolved route-less schema (complex pattern) for exDestination', {
                    exDestination,
                    ref,
                    resolvedSchema: resolvedSchema ? 'found' : 'not found',
                });
            }

            if (!resolvedSchema) {
                return null;
            }

            // Include $defs from base schema so nested $refs can be resolved
            return {
                ...resolvedSchema,
                $defs: baseSchema.$defs,
            };
        }

        // Pattern 1: Simple pattern with nested oneOf in then (e.g., JPM, CANACCORD)
        if (!exDestinationConditional?.then?.oneOf) {
            return null;
        }

        // Find the route-less conditional within the nested oneOf
        const routelessConditional = exDestinationConditional.then.oneOf.find((conditional: any) => {
            // Check if this is the route-less conditional:
            // if.allOf[0].properties.exDestination.const === exDestination
            // AND if.allOf[1].not.required includes 'route'
            if (!conditional?.if?.allOf || !Array.isArray(conditional.if.allOf)) {
                return false;
            }

            const hasMatchingExDestination = conditional.if.allOf.some((condition: any) => {
                return condition?.properties?.exDestination?.const === exDestination;
            });

            const hasNoRouteRequired = conditional.if.allOf.some((condition: any) => {
                return condition?.not?.required?.includes('route');
            });

            return hasMatchingExDestination && hasNoRouteRequired;
        });

        if (!routelessConditional?.then?.$ref) {
            return null;
        }

        // Resolve the $ref to get the actual schema using @jsonforms/core's resolveSchema
        const ref = routelessConditional.then.$ref;
        const refSchema = { $ref: ref };
        const resolvedSchema = resolveSchema(refSchema as JsonSchema, '', baseSchema);

        if (TRACE) {
            log.trace('Resolved route-less schema (simple pattern) for exDestination', {
                exDestination,
                ref,
                resolvedSchema: resolvedSchema ? 'found' : 'not found',
            });
        }

        if (!resolvedSchema) {
            return null;
        }

        // Include $defs from base schema so nested $refs can be resolved
        return {
            ...resolvedSchema,
            $defs: baseSchema.$defs,
        };
    }

    private isBasicSchema(baseSchema: BaseSchema): boolean {
        const defsKeys = Object.keys(baseSchema?.$defs ?? {});
        // Basic schema has only 'generic' or 'generic' + 'Instant' in $defs
        return (
            (defsKeys.length === 1 && defsKeys[0] === 'generic') ||
            (defsKeys.length === 2 && defsKeys.includes('generic') && defsKeys.includes('Instant'))
        );
    }
}
